import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { HttpService } from "services/http.service";
import { ServiceBase } from "services/servicebase";
import { ExcusedAbsenceModel } from "services/models/corporate/excusedAbsence.model";
import { Observable } from "rxjs";
import f from "odata-filter-builder";
import buildQuery from "odata-query";
import { ICDClassificationModel } from "services/models/corporate/icdClassification.model";
import { WorkLeaveTypeModel } from "services/models/corporate/workLeaveType.model";
import { AbsenceTypeModel } from "services/models/corporate/absenceType.model";
import { QueryParams } from "services/models/odata/queryParams";
import { Response } from "services/models/response";
import { map } from "rxjs/operators";
import { FileContentModel } from "services/models/fileContent";
import { ExcusedAbsenceQualificationContextModel } from "services/models/corporate/excusedAbsenceQualificationContext.model";

@Injectable()
export class ExcusedAbsenceService extends ServiceBase<ExcusedAbsenceModel> {
	constructor(http: HttpService) {
		super("excusedAbsence", http, environment.apiUrl);
	}

	createEntity(input: any): ExcusedAbsenceModel {
		return ExcusedAbsenceModel.create(input);
	}

	getContent(key: string, fileName: string): Observable<Blob> {
		return this.http.get(`excusedAbsence/view-document`, {
			params: { key, fileName },
			responseType: 'blob'
		});
	}
	
	listAbsencesByEmployee(
		params: QueryParams = null,
		employeeId: string
	): Observable<Response<ExcusedAbsenceModel[]>> {
		return this.customList<ExcusedAbsenceModel>(
			params, `list-by-employee/${employeeId}?`, ExcusedAbsenceModel
		);
	}

	listICDClassifications(params: QueryParams = null, char: string = null): Observable<ICDClassificationModel[]> {
		const defaultParams = {
			select: ["id", "code", "description"],
			orderBy: "code",
			orderByOrder: "asc"
		} as QueryParams;

		const finalParams = { ...(params || defaultParams) };

		if (char) {
			const filter = f()
				.contains("description", char.toUpperCase())
				.or(f().contains("code", char.toUpperCase()))
				.toString();

			finalParams.filter = buildQuery({ filter }).replace("?", "&");
		}

		return new Observable<any>((observer) => {
			this.customList<ICDClassificationModel>(finalParams,
				"icd-classifications/list", ICDClassificationModel, true
			).subscribe(
				(res) => {
					observer.next(res.data.filter((x) => x.enable));
				},
				(err) => {
					observer.error(err);
				}
			);
		});
	}

	listWorkLeaveTypes(params: QueryParams = null): Observable<WorkLeaveTypeModel[]> {
		const defaultParams = {
			select: ["id", "code", "description"],
			orderBy: "code",
			orderByOrder: "asc",
		} as QueryParams;

		return new Observable<any>((observer) => {
			this.customList<WorkLeaveTypeModel>(
				params || defaultParams,
				"work-leave-types/list",
				WorkLeaveTypeModel, true
			).subscribe((res) => {
				observer.next(res.data.filter((x) => x.enable));
			}),
				(err) => {
					observer.error(err);
				};
		});
	}

	listTypesByCompany(companyId: string): Observable<AbsenceTypeModel[]> {
		return this.http
			.get(`${this.endpoint}/types/list-by-company/${companyId}`, null, null, null, true)
			.pipe(
				map(res => {
					return res.body
						.filter((x: any) => x.enable)
						.map(x => AbsenceTypeModel.create(x));
				})
			);
	}

	listAbsenceTypes(params: QueryParams = null): Observable<AbsenceTypeModel[]> {
		const defaultParams = {
			select: ["id", "code", "description"],
			orderBy: "code",
			orderByOrder: "asc",
		} as QueryParams;

		return new Observable<any>((observer) => {
			this.customList<AbsenceTypeModel>(
				params || defaultParams,
				"types/list",
				AbsenceTypeModel, true
			).subscribe((res) => {
				observer.next(res.data.filter((x) => x.enable));
			}),
				(err) => {
					observer.error(err);
				};
		});
	}

	uploadTempFile(payload: {
		employeeId: string;
		dateBegin: string;
		fileContent: FileContentModel;
	}): Observable<Response<ExcusedAbsenceModel>> {
		return this.http
			.post(`${this.endpoint}/upload-temp-file`, payload, null, null, true)
			.pipe(
				map((res) => {
					const response = new Response<ExcusedAbsenceModel>();
					response.data = ExcusedAbsenceModel.create(res);
					return response;
				})
			);
	}

	registerByEmployee(model: ExcusedAbsenceModel): Observable<void> {
		return this.http
			.post(`${this.endpoint}/register-by-employee`, model, null, undefined, true)
			.pipe(map(() => undefined));
	}

	reproveExcusedAbsence(id: string, reproveReason: string): Observable<void> {
		return this.http
			.put(`${this.endpoint}/${id}/reprove`, { reproveReason }, true)
			.pipe(map(() => undefined));
	}

	getQualificationContext(id: string): Observable<ExcusedAbsenceQualificationContextModel> {
		return this.http
			.get(`${this.endpoint}/${id}/qualification-context`, null, null, null, true)
			.pipe(map((res) => ExcusedAbsenceQualificationContextModel.create(res.body ?? res)));
	}

}
